/**
 * UCM报文管理主页面
 * 提供UCM报文生成和历史记录查询功能
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Space, message, Tabs } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import UcmHistoryTable from './components/UcmHistoryTable';
import UcmHistoryFilter from './components/UcmHistoryFilter';
import UcmGenerateModal from './components/UcmGenerateModal';
import UcmInboundTab from './components/UcmInboundTab';
import ScmInboundTab from './components/ScmInboundTab';
import { ucmService } from '../../../api/enhancedApi';
import { DEFAULT_PAGINATION } from './constants/ucmConstants';

const UcmManagementPage = () => {
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
    const [filters, setFilters] = useState({});
    const [generateModalVisible, setGenerateModalVisible] = useState(false);

    // 加载历史记录列表
    const loadHistory = useCallback(async (params = {}) => {
        setLoading(true);
        try {
            // 合并参数，移除null/undefined值
            const queryParams = {
                page: pagination.current,
                page_size: pagination.pageSize,
                sort_by: 'created_at',
                sort_order: 'desc',
                ...filters,
                ...params,
            };
            
            // 移除空值，避免传递给后端
            Object.keys(queryParams).forEach(key => {
                if (queryParams[key] === null || queryParams[key] === undefined || queryParams[key] === '') {
                    delete queryParams[key];
                }
            });

            const response = await ucmService.getUcmHistory(queryParams);

            if (response.data) {
                setDataSource(response.data.items || []);
                setPagination(prev => ({
                    ...prev,
                    total: response.data.total || 0,
                    current: params.page || prev.current,
                }));
            }
        } catch (error) {
            console.error('加载UCM历史记录失败:', error);
            message.error('加载UCM历史记录失败');
        } finally {
            setLoading(false);
        }
    }, [pagination.current, pagination.pageSize, filters]);

    // 初始加载
    useEffect(() => {
        loadHistory();
    }, []);  // 只在组件挂载时执行一次

    // 处理表格变化（分页、排序等）
    const handleTableChange = (newPagination, filters, sorter) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));
        
        loadHistory({
            page: newPagination.current,
            page_size: newPagination.pageSize,
        });
    };

    // 处理筛选
    const handleFilter = (filterValues) => {
        setFilters(filterValues);
        setPagination(prev => ({ ...prev, current: 1 }));
        
        loadHistory({
            ...filterValues,
            page: 1,
            page_size: pagination.pageSize,
        });
    };

    // 重置筛选
    const handleResetFilter = () => {
        setFilters({});
        setPagination(prev => ({ ...prev, current: 1 }));
        
        loadHistory({
            page: 1,
            page_size: pagination.pageSize,
        });
    };

    // 刷新列表
    const handleRefresh = () => {
        loadHistory({
            page: pagination.current,
            page_size: pagination.pageSize,
        });
    };

    // 打开生成弹窗
    const handleOpenGenerateModal = () => {
        setGenerateModalVisible(true);
    };

    // 关闭生成弹窗
    const handleCloseGenerateModal = () => {
        setGenerateModalVisible(false);
    };

    // 生成成功后的回调
    const handleGenerateSuccess = () => {
        // 刷新列表，显示新生成的记录
        handleRefresh();
    };

    // 删除记录
    const handleDelete = async (id) => {
        try {
            await ucmService.deleteUcmHistory(id);
            message.success('删除成功');
            // 刷新列表
            handleRefresh();
        } catch (error) {
            console.error('删除UCM历史记录失败:', error);
            throw error;
        }
    };

    // 「报文生成」页签内容（既有功能）
    const generateTab = (
        <Card
            title="UCM 报文管理"
            extra={
                <Space>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleOpenGenerateModal}
                    >
                        生成 UCM 报文
                    </Button>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={handleRefresh}
                        loading={loading}
                    >
                        刷新
                    </Button>
                </Space>
            }
        >
            {/* 筛选条件 */}
            <UcmHistoryFilter
                onFilter={handleFilter}
                onReset={handleResetFilter}
                loading={loading}
            />

            {/* 历史记录表格 */}
            <UcmHistoryTable
                dataSource={dataSource}
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                onDelete={handleDelete}
            />
        </Card>
    );

    return (
        <div style={{ padding: '24px' }}>
            <Tabs
                defaultActiveKey="generate"
                items={[
                    { key: 'generate', label: 'UCM 报文生成', children: generateTab },
                    { key: 'inbound', label: 'UCM 报文解析', children: <UcmInboundTab /> },
                    { key: 'scm', label: 'SCM 报文解析', children: <ScmInboundTab /> },
                ]}
            />

            {/* 生成UCM报文弹窗 */}
            <UcmGenerateModal
                visible={generateModalVisible}
                onCancel={handleCloseGenerateModal}
                onSuccess={handleGenerateSuccess}
            />
        </div>
    );
};

export default UcmManagementPage;

